import React, { useState, useRef, useEffect } from 'react';

/**
 * Shared auth-profile picker used by Repeater, Intruder and the Scanner config.
 * Default selection is "Unknown (unauthenticated)" (value = ''), with the option
 * to pick an authenticated profile. Each option shows the profile's color dot,
 * username, role and when its session was last refreshed.
 *
 * Props:
 *   profiles  – array of AuthProfile objects
 *   value     – selected profile _id, or '' for unauthenticated
 *   onChange  – (idOrEmptyString) => void
 *   darkMode  – bool (component is inline-styled so it works in both Tailwind
 *               tabs and the inline-styled scan dialog)
 */
const timeAgo = (d) => {
  if (!d) return null;
  const ms = Date.now() - new Date(d).getTime();
  if (ms < 0) return 'just now';
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
};

const refreshedAt = (p) =>
  p?.sessionRefresh?.lastRefreshedAt || p?.proxyCaptured?.lastRefreshed || p?.updatedAt || null;

const usernameOf = (p) => p?.usernameForDuplicateDetection || p?.name || 'profile';

const ProfileSelect = ({ profiles = [], value = '', onChange, darkMode = true }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selected = profiles.find((p) => String(p._id) === String(value));

  const c = {
    bg: darkMode ? '#374151' : '#ffffff',
    bgHover: darkMode ? '#4b5563' : '#f3f4f6',
    menuBg: darkMode ? '#1f2937' : '#ffffff',
    border: darkMode ? '#4b5563' : '#d1d5db',
    text: darkMode ? '#f3f4f6' : '#1f2937',
    sub: darkMode ? '#9ca3af' : '#6b7280'
  };

  const Dot = ({ color }) => (
    <span style={{
      display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
      backgroundColor: color || '#9CA3AF', flex: '0 0 auto',
      boxShadow: color ? `0 0 0 2px ${color}22` : 'none'
    }} />
  );

  const rowStyle = {
    display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px',
    cursor: 'pointer', fontSize: 13, color: c.text, whiteSpace: 'nowrap'
  };

  const renderLabel = (p) => {
    if (!p) {
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <Dot color="#9CA3AF" />
          <span style={{ color: c.sub }}>Unknown (unauthenticated)</span>
        </span>
      );
    }
    const ago = timeAgo(refreshedAt(p));
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <Dot color={p.color} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{usernameOf(p)}</span>
        {p.role && <span style={{ color: c.sub, fontSize: 11 }}>· {p.role}</span>}
        {ago && <span style={{ color: c.sub, fontSize: 11 }}>· {ago}</span>}
      </span>
    );
  };

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 220 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 8, padding: '7px 10px', background: c.bg, color: c.text,
          border: `1px solid ${c.border}`, borderRadius: 6, fontSize: 13, cursor: 'pointer'
        }}
        title={selected ? `${usernameOf(selected)} — session refreshed ${refreshedAt(selected) ? new Date(refreshedAt(selected)).toLocaleString('sv-SE') : 'never'}` : 'Unauthenticated'}
      >
        {renderLabel(selected)}
        <span style={{ color: c.sub, fontSize: 10 }}>▾</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 10000,
          background: c.menuBg, border: `1px solid ${c.border}`, borderRadius: 6,
          boxShadow: '0 10px 30px rgba(0,0,0,0.35)', maxHeight: 280, overflowY: 'auto', minWidth: 260
        }}>
          <div
            style={rowStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = c.bgHover)}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            onClick={() => { onChange(''); setOpen(false); }}
          >
            {renderLabel(null)}
          </div>
          {profiles.map((p) => {
            const ago = timeAgo(refreshedAt(p));
            return (
              <div
                key={p._id}
                style={rowStyle}
                onMouseEnter={(e) => (e.currentTarget.style.background = c.bgHover)}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                onClick={() => { onChange(String(p._id)); setOpen(false); }}
              >
                <Dot color={p.color} />
                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{usernameOf(p)}</span>
                  <span style={{ color: c.sub, fontSize: 11 }}>
                    {p.role || 'unknown role'}{ago ? ` · refreshed ${ago}` : ''}
                  </span>
                </span>
              </div>
            );
          })}
          {profiles.length === 0 && (
            <div style={{ ...rowStyle, color: c.sub, cursor: 'default' }}>No profiles captured yet</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileSelect;
